import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useContext, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AppContext } from "src/contexts/AppContext";
import Header from "src/components/Layout/Header/Header";
import LeftMenu from "src/components/Layout/LeftMenu/LeftMenu";
import { CoreInterfaces, DTOs } from "./../core/Models";

import * as Constants from "src/core/Constants";
import {
  fetchApplicationConfigurationData,
  fetchBusinessOpportunityInfo,
  fetchCompanyDetails,
  fetchCustomerContactPersons,
  fetchEmployeesOffices,
  fetchEngagementConfigurationData,
  fetchGtContactPersons,
  fetchOfficeManagers,
  fetchSignedLettersForOrganisation,
  fetchTeams,
  fetchUserRoles,
  getAreAllServicesCompleted,
  getRelevantPageForNavigation,
  getUserInfo,
  isDigitallySigningChecked,
  isFinalorOutdatedVersion,
  updateStateOnEngagementConfigurationRead,
  wait,
} from "src/utils";
import { applyUpdatesOnQuestionChange } from "src/utils/processors/Questions";
import { useQuery, useRootConfig } from "src/core/Hooks/index";
import { ReactComponent as GTSpinner } from "./../assets/spinner.svg";
import { useTranslation } from "react-i18next";
import GtAlertBanner from "src/components/Common/GtAlertBanner";
import GTNotificationBanner from "src/components/Common/GTNotificationBanner";
import { useBeforeunload } from "react-beforeunload";
import { saveCurrentConfiguration } from "src/utils/handle-configuration-utils";
import { areApplicationConfVersionsChanged } from "src/utils/engagement-utils";
import { useScrollRestorer } from "src/core/Hooks/use-scroll-restorer";
import { LoadingContext } from "src/contexts/LoadingContext";
import { useErrorBoundary } from "react-error-boundary";
import GtSystemAdministrationMessages from "src/components/Common/GTSystemAdministrationMessages";
import GtExistingLettersDialog from "src/components/Common/GtExistingLettersDialog";
import { useSnackBar } from "src/contexts/SnackbarContext";
import { LicenseInfo } from "@mui/x-license";
import {
  isEmailContainingForbiddenCharactersForDigitalSigningUsers,
  isEmailMissingForDigitalSigningUsers,
} from "src/utils/generate-documents";
import { environment } from "src/core/Configurations/Environment.config";

export default function Root(): JSX.Element {
  LicenseInfo.setLicenseKey(environment.REACT_APP_MUI_X_LICENSE_KEY);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParamsMap = useQuery();
  const { dispatch, globalState, businessUnit } = useContext(AppContext);
  const { isLoading, loadingMessage, updateApplicationLoadingState } =
    useContext(LoadingContext);
  const { t } = useTranslation();
  const { ref: scrollPersisterRef } = useScrollRestorer();
  const { showBoundary } = useErrorBoundary();
  const rootConfig = useRootConfig();
  const { showSnackBar } = useSnackBar();

  const [existingSignedLetters, setExistingSignedLetters] = useState<
    Array<CoreInterfaces.SignedEngagementLetter>
  >([]);
  const [isExistingLettersDialogOpen, setIsExistingLettersDialogOpen] =
    useState(false);
  const [hasCheckedExistingLetters, setHasCheckedExistingLetters] =
    useState(false);

  const businessOpportunityId = queryParamsMap.get(
    Constants.QueryParams.businessOpportunityId
  );
  const clientId = queryParamsMap.get(Constants.QueryParams.clientId);
  const unit = queryParamsMap.get(Constants.QueryParams.unit);
  const engagementConfigurationId = queryParamsMap.get(
    Constants.QueryParams.engagementConfigurationId
  );
  const id = globalState.currentConfiguration.id;
  const areAllServicesCompleted = getAreAllServicesCompleted(
    globalState.services.filter(
      (service: DTOs.ServiceDTO) => service.state.isSelected
    ),
    businessUnit
  );
  const isGenerateDocumentsPage = location.pathname.endsWith(
    Constants.Routes.GenerateDocuments
  );

  const isQuestionPage = location.pathname.endsWith(Constants.Routes.Question);

  const rateschangesMessage = {
    id: "RateschangesMsg",
    message: "General.RatesChangesMessage",
    title: "General.RatesChangesTitle",
    type: Constants.SystemAdministrationMessageTypes.Alert,
    state: {
      isShown: false,
    },
  };
  const isFinalorOutdatedVersionStatus = isFinalorOutdatedVersion(globalState);
  const globalStateRef = useRef(globalState);
  globalStateRef.current = globalState;

  const shouldDisplayRatesChangeMessage =
    businessUnit === Constants.BusinessUnit.Outsourcing &&
    globalState.state.isRatesChangeMessagesShow &&
    areApplicationConfVersionsChanged(globalState) &&
    !isFinalorOutdatedVersionStatus;
  const isPriceAndTimeCalculationPage = location.pathname.endsWith(
    Constants.Routes.PriceAndTimeCalculation
  );
  const hasSelectedServices = globalState.services.some(
    (service) =>
      service.state.canBeSelectedInServicePage && service.state.isSelected
  );

  function retrieveRemoteData(): Promise<void> {
    return Promise.all([
      fetchCustomerContactPersons(
        Constants.APIPath.ContactPersonsForGeneralInfo.replace(
          "{upsalescustomerid}",
          clientId
        )
      ),
      fetchGtContactPersons(),
      fetchCompanyDetails(
        Constants.APIPath.CompanyDetails.replace(
          "{upsalescustomerid}",
          clientId
        )
      ),
      fetchBusinessOpportunityInfo(businessOpportunityId),
      fetchEmployeesOffices(),
      fetchUserRoles(),
      fetchOfficeManagers(),
      fetchTeams(),
    ])
      .then(
        ([
          customerContactPersons,
          gtContactPersons,
          companyDetails,
          businessOpportunityInfo,
          employeesOffices,
          userRoles,
          officeManagers,
          teams,
        ]) => {
          dispatch({
            type: Constants.AppStateActions.UpdateRemoteData,
            payload: {
              remoteData: {
                customerContactPersons,
                gtContactPersons,
                companyDetails,
                businessOpportunityInfo,
                employeesOffices,
                userRoles,
                officeManagers,
                teams,
              },
            },
          });
        }
      )
      .catch((err) => {
        showBoundary(err);
      });
  }

  useBeforeunload(() => {
    if (
      globalState.currentConfiguration.id &&
      !isFinalorOutdatedVersionStatus &&
      globalState.areUnsavedChanges
    ) {
      saveCurrentConfiguration(
        globalState,
        dispatch,
        businessOpportunityId,
        unit,
        clientId
      );
      wait(500);
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (
        globalStateRef.current.currentConfiguration.id &&
        !isFinalorOutdatedVersion(globalStateRef.current) &&
        !globalStateRef.current.isConflictOnUpdateEngagement &&
        globalStateRef.current.areUnsavedChanges &&
        Date.now() - globalStateRef.current.unsavedChangesTimestamp >
          +environment.REACT_APP_AUTOSAVE_INTERVAL
      ) {
        saveCurrentConfiguration(
          globalStateRef.current,
          dispatch,
          businessOpportunityId,
          unit,
          clientId
        ).then(() => {
          showSnackBar(t("General.AutoSaveMessage"), "success", 3000);
        });
        dispatch({
          type: Constants.AppStateActions.ChangeUnsavedFlag,
        });
      }
    }, +environment.REACT_APP_AUTOSAVE_INTERVAL);
    return () => clearTimeout(timer);
  }, [globalState.unsavedChangesTimestamp]);

  useEffect(() => {
    if (!globalState.authProviderUserDetails?.userId) {
      (async () => {
        const authProviderUserDetails = await getUserInfo();

        if (!authProviderUserDetails) {
          window.location.assign(
            `${
              Constants.AadApiPath.AuthLogin
            }?post_login_redirect_uri=${encodeURIComponent(
              location.pathname + location.search
            )}`
          );
          return;
        }

        dispatch({
          type: Constants.AppStateActions.UpdateAadUserInfo,
          payload: {
            authProviderUserDetails,
          },
        });
      })();
    }

    if (
      !!businessOpportunityId &&
      !!clientId &&
      globalState.authProviderUserDetails?.userId
    ) {
      const fetchDataFn = () => {
        updateApplicationLoadingState(true);
        fetchApplicationConfigurationData()
          .then((applicationConfigurationData) => {
            Promise.all([
              fetchEngagementConfigurationData(
                businessOpportunityId,
                engagementConfigurationId
              ),
              retrieveRemoteData(),
            ])
              .then(([engagementConfigurationData]) => {
                if (!!applicationConfigurationData) {
                  dispatch({
                    type: Constants.AppStateActions
                      .UpdateApplicationConfiguration,
                    payload: applicationConfigurationData,
                  });
                }

                dispatch({
                  type: Constants.AppStateActions
                    .ApplicationConfigurationVersionsChanged,
                  payload: {
                    rateCart:
                      applicationConfigurationData.RateCartConfiguration
                        .Version,
                    taskCost:
                      applicationConfigurationData.TaskCostConfiguration
                        .Version,
                    taskAllocation:
                      applicationConfigurationData.TaskAllocationConfiguration
                        .Version,
                    subsidiariesConfiguration:
                      applicationConfigurationData.SubsidiaryConfiguration,
                  },
                });

                dispatch({
                  type: Constants.AppStateActions
                    .RatesChangeMessageVisibilityUpdated,
                  payload: true,
                });

                updateStateOnEngagementConfigurationRead(
                  engagementConfigurationData,
                  dispatch,
                  (service: DTOs.ServiceDTO) =>
                    applyUpdatesOnQuestionChange(globalState, service),
                  applicationConfigurationData
                );
                const nextRoutesMap = {
                  [Constants.ConfigurationStatus.Active]:
                    Constants.Routes.Question,
                  [Constants.ConfigurationStatus.FinalVersion]:
                    Constants.Routes.GenerateDocuments,
                  [Constants.ConfigurationStatus.OutdatedVersion]:
                    Constants.Routes.GenerateDocuments,
                };
                const relevantPageForNavigation = getRelevantPageForNavigation(
                  engagementConfigurationData?.Status,
                  nextRoutesMap,
                  businessOpportunityId,
                  clientId,
                  unit,
                  engagementConfigurationId ?? engagementConfigurationData?.Id
                );
                if (relevantPageForNavigation) {
                  navigate(relevantPageForNavigation, { replace: true });
                } else if (
                  engagementConfigurationData?.Id &&
                  engagementConfigurationData?.Id !== engagementConfigurationId
                ) {
                  const urlSearchParams = new URLSearchParams(
                    window.location.search
                  );
                  urlSearchParams.set(
                    Constants.QueryParams.engagementConfigurationId,
                    engagementConfigurationData.Id
                  );
                  navigate(`?${urlSearchParams}`, { replace: true });
                }
              })
              .catch((err: Error) => showBoundary(err))
              .finally(() => {
                updateApplicationLoadingState(false);
              });
          })
          .catch((err: Error) => showBoundary(err));
      };
      if (rootConfig.isEngagementApplicationPage) {
        fetchDataFn();
      }
    }
  }, [
    businessOpportunityId,
    clientId,
    globalState.authProviderUserDetails?.userId,
    rootConfig.isEngagementApplicationPage,
  ]);

  useEffect(() => {
    const organisationNumber =
      globalState.remoteData?.companyDetails?.OrgNr ?? null;
    const isBrandNewEngagement =
      !engagementConfigurationId && !globalState.currentConfiguration.id;

    if (
      rootConfig.isEngagementApplicationPage &&
      !isLoading &&
      unit === Constants.BusinessUnit.Outsourcing &&
      isBrandNewEngagement &&
      !hasCheckedExistingLetters &&
      !!organisationNumber
    ) {
      setHasCheckedExistingLetters(true);
      // Keep the page in a loading state while we check for existing letters so the
      // user cannot start working during the window before the dialog appears. Once
      // the dialog is shown it is modal and blocks the page via its own backdrop.
      updateApplicationLoadingState(true);
      fetchSignedLettersForOrganisation(organisationNumber)
        .then((signedLetters) => {
          if (!!signedLetters && signedLetters.length > 0) {
            setExistingSignedLetters(signedLetters);
            setIsExistingLettersDialogOpen(true);
          }
          updateApplicationLoadingState(false);
        })
        .catch(() => {
          // Failing to load existing letters should not block creating a new engagement
          updateApplicationLoadingState(false);
        });
    }
  }, [
    globalState.remoteData?.companyDetails?.OrgNr,
    globalState.currentConfiguration.id,
    engagementConfigurationId,
    unit,
    isLoading,
    hasCheckedExistingLetters,
    rootConfig.isEngagementApplicationPage,
  ]);

  useEffect(() => {
    const fetchDataFn = () => {
      fetchApplicationConfigurationData()
        .then((applicationConfigurationData) => {
          Promise.all([
            fetchGtContactPersons(),
            fetchEmployeesOffices(),
            fetchUserRoles(),
            fetchOfficeManagers(),
            fetchTeams(),
          ])
            .then(
              ([
                gtContactPersons,
                employeesOffices,
                userRoles,
                officeManagers,
                teams,
              ]) => {
                dispatch({
                  type: Constants.AppStateActions.UpdateRemoteData,
                  payload: {
                    remoteData: {
                      gtContactPersons,
                      employeesOffices,
                      userRoles,
                      officeManagers,
                      teams,
                    },
                  },
                });

                if (!!applicationConfigurationData) {
                  dispatch({
                    type: Constants.AppStateActions
                      .UpdateApplicationConfiguration,
                    payload: applicationConfigurationData,
                  });
                }

                dispatch({
                  type: Constants.AppStateActions
                    .ApplicationConfigurationVersionsChanged,
                  payload: {
                    rateCart:
                      applicationConfigurationData.RateCartConfiguration
                        .Version,
                    taskCost:
                      applicationConfigurationData.TaskCostConfiguration
                        .Version,
                    taskAllocation:
                      applicationConfigurationData.TaskAllocationConfiguration
                        .Version,
                    subsidiariesConfiguration:
                      applicationConfigurationData.SubsidiaryConfiguration,
                  },
                });

                dispatch({
                  type: Constants.AppStateActions
                    .RatesChangeMessageVisibilityUpdated,
                  payload: true,
                });
              }
            )
            .catch((err: Error) => showBoundary(err))
            .finally(() => {
              updateApplicationLoadingState(false);
            });
        })
        .catch((err: Error) => showBoundary(err));
    };
    if (
      !rootConfig.isEngagementApplicationPage &&
      !globalState.currentConfiguration.applicationConfiguration
    ) {
      fetchDataFn();
    }
  }, [
    globalState.authProviderUserDetails?.userId,
    rootConfig.isEngagementApplicationPage,
  ]);

  if (!globalState.authProviderUserDetails?.userId) {
    return null;
  }

  if (
    (!businessOpportunityId || !clientId) &&
    rootConfig.isEngagementApplicationPage
  ) {
    const queryParams = new URLSearchParams();
    queryParams.set(
      Constants.QueryParams.businessOpportunityId,
      Date.now().toString()
    );
    queryParams.set(Constants.QueryParams.clientId, "20");
    queryParams.set(Constants.QueryParams.unit, "es");
    const navigationURL = `?${queryParams}`;
    return (
      <article className="gt-invalidApplicationScreen">
        <h2>{t("General.InvalidApplicationURL")}</h2>
        {(environment.REACT_APP_ENV === Constants.Environments.DEV ||
          environment.REACT_APP_ENV === Constants.Environments.TEST) && (
          <NavLink to={navigationURL}>{t("General.LoadSampleApp")}</NavLink>
        )}
        {environment.REACT_APP_ENV !== Constants.Environments.DEV &&
          environment.REACT_APP_ENV !== Constants.Environments.TEST && (
            <div className="gt-invalidApplicationScreen__warningMessage">
              <WarningAmberIcon fontSize="medium" />
              <span>{t("General.StartJourneyMessage")}</span>
            </div>
          )}
      </article>
    );
  }

  let isEmailMissingDigitalSigningUsers = false;
  if (isGenerateDocumentsPage) {
    isEmailMissingDigitalSigningUsers =
      isEmailMissingForDigitalSigningUsers(globalState);
  }

  let isEmailContainingForbiddenCharactersSigningUsers = false;
  if (isGenerateDocumentsPage) {
    isEmailContainingForbiddenCharactersSigningUsers =
      isEmailContainingForbiddenCharactersForDigitalSigningUsers(globalState);
  }

  let shouldTaxWarningBannerAppears = false;
  let shouldServicesAgreementTaxWarningBannerAppears = false;

  if (businessUnit === Constants.BusinessUnit.Tax) {
    shouldTaxWarningBannerAppears = globalState.services.some((service) => {
      return (
        (service.data.code === Constants.ServiceCode.CounselingTaxLetter ||
          service.data.code === Constants.ServiceCode.ServiceAgreement) &&
        service.state.isSelected
      );
    });

    shouldServicesAgreementTaxWarningBannerAppears = globalState.services.some(
      (service) => {
        return (
          service.data.code === Constants.ServiceCode.ServiceAgreement &&
          service.state.isSelected
        );
      }
    );
  }

  return (
    <article
      className={`gt-appRoot ${isLoading ? "gt-appRoot--loadingState" : ""} ${
        globalState.state.isPageBlurred ? "gt-appRoot--loadingBlurState" : ""
      } ${
        shouldDisplayRatesChangeMessage &&
        isPriceAndTimeCalculationPage &&
        hasSelectedServices
          ? "gt-appRoot--withPurpleBackground"
          : ""
      }`}
    >
      <section className="gt-appRoot__loadingContainer">
        <div className="gt-appRoot__loadingContainer-spinner">
          <GTSpinner />
        </div>
        <div className="gt-appRoot__loadingContainer-loadingText">
          {loadingMessage === null ? t("General.Loading") : loadingMessage}
        </div>
      </section>
      <Header
        areCtasButtonsHidden={rootConfig.areCtasButtonsHidden}
        isBusinessUnit={rootConfig.isBusinessUnit}
        isOfferStatusHidden={rootConfig.isOfferStatusHidden}
        isVersionNameHidden={rootConfig.isVersionNameHidden}
        isOwnerNameHidden={rootConfig.isOwnerNameHidden}
        areBreadcrumbsHidden={rootConfig.areBreadcrumbsHidden}
      />
      <main className="gt-appRoot__main">
        {!rootConfig.isLeftSideBarHidden && <LeftMenu />}
        <div ref={scrollPersisterRef} className="gt-appRoot__main-container">
          {isQuestionPage && shouldTaxWarningBannerAppears && (
            <GtAlertBanner
              alertBannerVisibleUS={true}
              message={t("General.WarningTaxLetter")}
              variant={Constants.GtAlertBannerVariant.QuestionPage}
            />
          )}
          {isQuestionPage && shouldServicesAgreementTaxWarningBannerAppears && (
            <GtAlertBanner
              alertBannerVisibleUS={false}
              message={t("General.WarningServicesAgreementLetter")}
              variant={Constants.GtAlertBannerVariant.QuestionPage}
            />
          )}
          {!id && !rootConfig.isAlertBannerHidden && (
            <GtAlertBanner
              message={t("General.NotSavedMessage")}
              withCloseButton={false}
            />
          )}
          {id &&
            isGenerateDocumentsPage &&
            !areAllServicesCompleted &&
            !isFinalorOutdatedVersionStatus && (
              <GtAlertBanner
                message={t("General.GenerateDocumentsWarningMessage")}
                withCloseButton={false}
              />
            )}
          {isEmailMissingDigitalSigningUsers &&
            isDigitallySigningChecked(globalState) && (
              <GtAlertBanner
                message={t("General.DigitallySigneeMissingEmail")}
                withCloseButton={false}
              />
            )}
          {!isEmailMissingDigitalSigningUsers &&
            isEmailContainingForbiddenCharactersSigningUsers &&
            isDigitallySigningChecked(globalState) && (
              <GtAlertBanner
                message={t("General.DigitallySigneeForbiddenCharactersEmail")}
                withCloseButton={false}
              />
            )}
          <GtSystemAdministrationMessages />
          {shouldDisplayRatesChangeMessage &&
            !rootConfig.isNotificationBannerHidden && (
              <GTNotificationBanner
                key={`banner${rateschangesMessage.id}`}
                systemAdministrationMessage={rateschangesMessage}
                shouldTranslateValue={true}
                onBannerClose={() => {
                  dispatch({
                    type: Constants.AppStateActions
                      .RatesChangeMessageVisibilityUpdated,
                    payload: false,
                  });
                }}
              />
            )}
          <Outlet />
        </div>
      </main>
      <GtExistingLettersDialog
        isOpen={isExistingLettersDialogOpen}
        letters={existingSignedLetters}
        onCreateNew={() => setIsExistingLettersDialogOpen(false)}
        onClose={() => setIsExistingLettersDialogOpen(false)}
      />
    </article>
  );
}
